import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Send } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface SupportRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SupportRequestDialog: React.FC<SupportRequestDialogProps> = ({
  open,
  onOpenChange
}) => {
  useUiLanguage();
  const { t } = useTranslation(['admin']);
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !profile) {
      toast({
        title: uiText("Error"),
        description: uiText("You must be logged in to submit a support request."),
        variant: "destructive"
      });
      return;
    }

    if (!formData.subject.trim() || !formData.message.trim()) {
      toast({
        title: t('admin:support.validationError'),
        description: t('admin:support.fillAllFields'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('support_requests')
        .insert({
          admin_id: user.id,
          dojo_id: profile.dojo_id,
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          priority: formData.priority
        });

      if (error) throw error;

      toast({
        title: t('admin:support.requestSent'),
        description: t('admin:support.requestSentDescription'),
      });

      // Reset form
      setFormData({
        subject: '',
        message: '',
        priority: 'normal'
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast({
        title: t('admin:support.requestFailed'),
        description: t('admin:support.requestFailedDescription'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-dojo-primary" />
            {t('admin:support.requestSupport')}
          </DialogTitle>
          <DialogDescription>
            {t('admin:support.requestSupportDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">{t('admin:support.subject')} *</Label>
            <Input
              id="subject"
              placeholder={t('admin:support.subjectPlaceholder')}
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">{t('admin:support.priority')}</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => handleInputChange('priority', value)}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('admin:support.priorityLow')}</SelectItem>
                <SelectItem value="normal">{t('admin:support.priorityNormal')}</SelectItem>
                <SelectItem value="high">{t('admin:support.priorityHigh')}</SelectItem>
                <SelectItem value="urgent">{t('admin:support.priorityUrgent')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t('admin:support.message')} *</Label>
            <Textarea
              id="message"
              placeholder={t('admin:support.messagePlaceholder')}
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              disabled={isSubmitting}
              rows={4}
              className="resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('common:cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gap-2"
            >
              {isSubmitting ? (
                t('admin:support.sending')
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {t('admin:support.sendRequest')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportRequestDialog;